/**
 * Local progress kept on this device:
 *
 * - `savedRating` / `peakRating` — warm start for runs the server can't issue
 *   (offline demo backend, offline tickets). The server stays authoritative when
 *   it is reachable; ApiBackend mirrors the server's saved rating here so an
 *   offline run still starts near the player's level.
 * - `highestLevelReached` — the highest level any run on this device reached
 *   (RunSummary.levelReached), which unlocks that level's theme in the
 *   Profile's theme gallery (GAME_DESIGN "every level a new theme to unlock").
 *   The server keeps no per-player "level reached" (GET /api/players/me only
 *   returns the saved rating), so a server rating mirrored here also raises it
 *   to the level that rating implies.
 * - `stickers` — cosmetic theme stickers earned from opt-in rewarded ads
 *   (never an in-run advantage). Only themes the player has reached can be
 *   earned (screens/model/onboarding.ts § nextStickerId).
 * - `onboardingPromptDismissed` — the player said "not now" to joining the
 *   leaderboard; don't nag after every run.
 */
import { useSyncExternalStore } from 'react';
import { LEVEL_COUNT, levelForRating, warmStartRating } from '@dropstack/core';
import { createObservable, type Observable } from './observable.ts';
import { getAppStorage, isFiniteNumber, isRecord, STORAGE_KEYS, type JsonStore } from './storage.ts';

export interface Progress {
  /** Rating to warm-start the next run from (null before the first finished run). */
  savedRating: number | null;
  /**
   * Highest saved rating so far: the local stand-in for the server's
   * referenceRating (anti-farming match factor). Null before the first run.
   */
  peakRating: number | null;
  /** Highest level reached in any run (1–LEVEL_COUNT); level 1 is where everyone starts. */
  highestLevelReached: number;
  /** Earned cosmetic sticker ids (theme ids), in the order they were earned. */
  stickers: string[];
  onboardingPromptDismissed: boolean;
}

export const EMPTY_PROGRESS: Readonly<Progress> = Object.freeze({
  savedRating: null,
  peakRating: null,
  highestLevelReached: 1,
  stickers: [],
  onboardingPromptDismissed: false,
});

/** Start/reference ratings for a run issued on this device (offline play). */
export function localRatings(progress: Progress): { startRating: number; referenceRating: number } {
  const startRating = warmStartRating(progress.savedRating);
  return { startRating, referenceRating: Math.max(progress.peakRating ?? startRating, startRating) };
}

export function loadProgress(store: JsonStore = getAppStorage()): Progress {
  return store.get(STORAGE_KEYS.progress, decodeProgress) ?? { ...EMPTY_PROGRESS, stickers: [] };
}

export function saveProgress(progress: Progress, store: JsonStore = getAppStorage()): boolean {
  return store.set(STORAGE_KEYS.progress, progress);
}

// ───────────── Store + hook ─────────────

export interface ProgressStore extends Observable<Progress> {
  /**
   * Record the rating to persist after a run (RunSummary.savedRating, or the
   * server's savedRating). Updates the peak too, and raises
   * highestLevelReached to the level climbed to from level 1 at that rating
   * (a conservative lower bound: the level switch needs the hysteresis margin).
   */
  recordRating(savedRating: number): void;
  /** Record a finished run's RunSummary.levelReached (only ever raises highestLevelReached). */
  recordLevelReached(level: number): void;
  /** Add a sticker; returns false when it was already collected. */
  addSticker(id: string): boolean;
  hasSticker(id: string): boolean;
  setOnboardingPromptDismissed(dismissed: boolean): void;
  /** Settings → "Reset progress". Stickers are kept unless `keepStickers` is false. */
  reset(options?: { keepStickers?: boolean }): void;
}

export function createProgressStore(store: JsonStore = getAppStorage()): ProgressStore {
  const value = createObservable(loadProgress(store));
  const commit = (next: Progress) => {
    saveProgress(next, store);
    value.set(next);
  };
  return {
    get: value.get,
    subscribe: value.subscribe,
    recordRating(savedRating) {
      if (!isFiniteNumber(savedRating)) return;
      const current = value.get();
      commit({
        ...current,
        savedRating,
        peakRating: Math.max(current.peakRating ?? savedRating, savedRating),
        highestLevelReached: Math.max(current.highestLevelReached, levelForRating(savedRating, 1)),
      });
    },
    recordLevelReached(level) {
      const current = value.get();
      const reached = toLevel(level);
      if (reached !== null && reached > current.highestLevelReached) commit({ ...current, highestLevelReached: reached });
    },
    addSticker(id) {
      const current = value.get();
      if (current.stickers.includes(id)) return false;
      commit({ ...current, stickers: [...current.stickers, id] });
      return true;
    },
    hasSticker: (id) => value.get().stickers.includes(id),
    setOnboardingPromptDismissed(dismissed) {
      const current = value.get();
      if (current.onboardingPromptDismissed !== dismissed) commit({ ...current, onboardingPromptDismissed: dismissed });
    },
    reset(options = {}) {
      const stickers = options.keepStickers === false ? [] : value.get().stickers;
      commit({ ...EMPTY_PROGRESS, stickers });
    },
  };
}

let appProgress: ProgressStore | null = null;

/** The app-wide progress store (localStorage-backed), created on first use. */
export function getProgressStore(): ProgressStore {
  appProgress ??= createProgressStore();
  return appProgress;
}

/** React hook: current local progress (re-renders when it changes). */
export function useProgress(store: ProgressStore = getProgressStore()): Progress {
  return useSyncExternalStore(store.subscribe, store.get, store.get);
}

// ───────────── Decoding ─────────────

function decodeProgress(raw: unknown): Progress | null {
  if (!isRecord(raw)) return null;
  const rating = (x: unknown): number | null => (isFiniteNumber(x) && x >= 0 ? x : null);
  const stickers = Array.isArray(raw.stickers)
    ? [...new Set(raw.stickers.filter((s): s is string => typeof s === 'string' && s.length > 0))]
    : [];
  return {
    savedRating: rating(raw.savedRating),
    peakRating: rating(raw.peakRating),
    highestLevelReached: toLevel(raw.highestLevelReached) ?? 1,
    stickers,
    onboardingPromptDismissed: raw.onboardingPromptDismissed === true,
  };
}

/** A whole level number 1–LEVEL_COUNT, or null. */
function toLevel(x: unknown): number | null {
  return isFiniteNumber(x) && Number.isInteger(x) && x >= 1 && x <= LEVEL_COUNT ? x : null;
}
